import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import ToolsLanguagesBannerImage from "../../../assets/services/asic-design/asicDesign2.png";
import ProfileImage from "../../../assets/blogpages/person.png";
import "./ToolsAndLanguages.css";
import { AnimatePresence, motion } from "framer-motion";

// Simple highlight component: wraps matches in <mark>
const Highlight = ({ text = "", query = "" }) => {
  if (!query) return <>{text}</>;
  const parts = text.split(new RegExp(`(${query})`, "gi"));
  return parts.map((part, i) =>
    part.toLowerCase() === query.toLowerCase()
      ? <mark key={i}>{part}</mark>
      : part
  );
};

const ToolsAndLanguages = () => {
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [replyVisible, setReplyVisible] = useState({});

  const closeOverlay = () => setShowPopup(false);
  const handleSearch = () => setSearchTerm(searchInput.trim());
  const handleCommentSubmit = e => {
    e.preventDefault();
    setShowPopup(true);
    e.target.reset();
  };
  const handleReplySubmit = e => {
    e.preventDefault();
    setShowPopup(true);
    e.target.reset();
  };
  const toggleReply = id =>
    setReplyVisible(prev => ({ ...prev, [id]: !prev[id] }));

  const sectionRefs = {
    intro: useRef(null),
    languages: useRef(null),
    edaTools: useRef(null),
    scripting: useRef(null),
    frameworks: useRef(null),
    versionControl: useRef(null),
    debugTrace: useRef(null),
    coverage: useRef(null),
    ciCd: useRef(null),
    community: useRef(null),
    trends: useRef(null),
    comments: useRef(null),
  };
  const scrollTo = ref =>
    ref.current && ref.current.scrollIntoView({ behavior: "smooth", block: "center" });

  // scroll to first mark on search
  useEffect(() => {
    if (!searchTerm) return;
    const first = document.querySelector("mark");
    first?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [searchTerm]);

  // fade-in/out on scroll
  useEffect(() => {
    const io = new IntersectionObserver(
      entries => entries.forEach(e => {
        e.isIntersecting
          ? e.target.classList.add("visible")
          : e.target.classList.remove("visible");
      }),
      { threshold: 0.1 }
    );
    document.querySelectorAll(".fade-section").forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);

  // auto-dismiss popup
  useEffect(() => {
    if (showPopup) {
      const t = setTimeout(() => setShowPopup(false), 2000);
      return () => clearTimeout(t);
    }
  }, [showPopup]);

  return (
    <div className="blog-post-page tools-languages">
      {/* Banner */}
      <div className="blog-banner fade-section">
        <div className="banner-overlay">
          <h1>Tools & Languages</h1>
          <div className="breadcrumb">
            <Link to="/services/asic-design">Services</Link> | Tools & Languages
          </div>
        </div>
      </div>

      {/* Mobile nav */}
      <div className="mobile-nav fade-section">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search keyword..."
            className="search-input"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
          />
          <button onClick={handleSearch} className="search-button">Search</button>
        </div>
        <div className="table-of-contents">
          <h4>In This Article</h4>
          <ul>
            {Object.entries(sectionRefs).map(([key, ref]) => (
              <li key={key}>
                <button onClick={() => scrollTo(ref)}>
                  {{
                    intro: "Introduction",
                    languages: "Hardware Description Languages",
                    edaTools: "EDA Tools & Suites",
                    scripting: "Scripting & Automation",
                    frameworks: "Verification Frameworks",
                    versionControl: "Version Control",
                    debugTrace: "Debug & Trace Tools",
                    coverage: "Coverage & Metrics",
                    ciCd: "CI/CD & Pipelines",
                    community: "Open-Source & Community",
                    trends: "Future Trends",
                    comments: "Comments",
                  }[key]}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Content */}
      <div className="blog-content-container">
        <div className="blog-left-column">
          <img
            src={ToolsLanguagesBannerImage}
            alt="Tools and Languages"
            className="post-main-image fade-section"
          />
          <div className="post-info-row fade-section">
            <span className="post-author">By Alex Engineer</span>
            <span className="post-category">Category: ASIC Development</span>
            <button
              className="comment-link"
              onClick={() => scrollTo(sectionRefs.comments)}
            >
              3 Comments
            </button>
          </div>

          {/* 1. Introduction */}
          <h2 ref={sectionRefs.intro} className="fade-section">Introduction</h2>
          <p className="fade-section">
            <Highlight
              text="The ASIC industry relies on a rich ecosystem of languages and tools, spanning RTL description, simulation, synthesis, place-and-route, formal verification, and post-silicon validation."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="This article surveys the key hardware description languages, EDA suites, scripting environments, verification frameworks, and CI/CD integrations that underpin modern ASIC design and verification flows."
              query={searchTerm}
            />
          </p>

          {/* 2. Hardware Description Languages */}
          <h2 ref={sectionRefs.languages} className="fade-section">Hardware Description Languages</h2>
          <ul className="fade-section">
            {[
              "SystemVerilog – the lingua franca for RTL, assertions (SVA), UVM testbenches",
              "VHDL – used in FPGA and military applications for strong typing and readability",
              "Verilog – legacy widespread support, often for simple testbenches or IP cores",
              "Chisel – emerging Scala-based HDL for parameterized generators",
              "Bluespec – high-level guarded atomic actions style HDL",
            ].map((item, idx) => (
              <li key={idx}>
                <Highlight text={item} query={searchTerm} />
              </li>
            ))}
          </ul>

          {/* 3. EDA Tools & Suites */}
          <h2 ref={sectionRefs.edaTools} className="fade-section">EDA Tools & Suites</h2>
          <p className="fade-section">
            <Highlight
              text="Major vendors provide integrated suites covering synthesis, place-and-route, timing analysis, and equivalence checking:"
              query={searchTerm}
            />
          </p>
          <ul className="fade-section">
            {[
              "Cadence Genus, Innovus, Tempus, JasperGold",
              "Synopsys Design Compiler, IC Compiler II, PrimeTime, VC Formal",
              "Mentor Olympus-SoC, Tessent DFT, Calibre DRC/LVS",
              "OpenROAD – open-source flow for RTL2GDS",
              "Yosys + nextpnr – community-driven for FPGA prototyping",
            ].map((tool, idx) => (
              <li key={idx}>
                <Highlight text={tool} query={searchTerm} />
              </li>
            ))}
          </ul>

          {/* 4. Scripting & Automation */}
          <h2 ref={sectionRefs.scripting} className="fade-section">Scripting & Automation</h2>
          <p className="fade-section">
            <Highlight
              text="Automation underpins every stage. Popular scripting languages and frameworks include:"
              query={searchTerm}
            />
          </p>
          <ul className="fade-section">
            {[
              "Tcl – ubiquitous in EDA tool APIs for flow control and tool invocation",
              "Python – for complex automation, data processing, web dashboards (Flask, Django)",
              "Perl/Bash – legacy glue scripts and CI integrations",
              "Make/CMake – build orchestration, dependency management",
            ].map((lang, idx) => (
              <li key={idx}>
                <Highlight text={lang} query={searchTerm} />
              </li>
            ))}
          </ul>

          {/* 5. Verification Frameworks */}
          <h2 ref={sectionRefs.frameworks} className="fade-section">Verification Frameworks</h2>
          <p className="fade-section">
            <Highlight
              text="Standardized environments accelerate testbench development:"
              query={searchTerm}
            />
          </p>
          <ul className="fade-section">
            {[
              "UVM (Universal Verification Methodology) – class-based, layered testbench",
              "OVM/VMM – predecessors to UVM, still in use in some flows",
              "Cocotb – Python-based coroutine testbenches",
              "Google SVUnit – unit testing framework for SystemVerilog",
            ].map((fw, idx) => (
              <li key={idx}>
                <Highlight text={fw} query={searchTerm} />
              </li>
            ))}
          </ul>

          {/* 6. Version Control */}
          <h2 ref={sectionRefs.versionControl} className="fade-section">Version Control</h2>
          <ul className="fade-section">
            {[
              "Git – de facto standard, with submodules for IP and LFS for large files",
              "Perforce – chosen by some enterprises for monolithic repos and binary storage",
              "SVN – legacy flows",
            ].map((vc, idx) => (
              <li key={idx}>
                <Highlight text={vc} query={searchTerm} />
              </li>
            ))}
          </ul>

          {/* 7. Debug & Trace Tools */}
          <h2 ref={sectionRefs.debugTrace} className="fade-section">Debug & Trace Tools</h2>
          <ul className="fade-section">
            {[
              "Waveform viewers: Verdi, SimVision, DVE",
              "Logic Analyzers: Tektronix, Keysight, or embedded trace (Arm CoreSight)",
              "Coverage analysis: Incisive Coverage, VCS gcov, Verilator gcov",
            ].map((dbg, idx) => (
              <li key={idx}>
                <Highlight text={dbg} query={searchTerm} />
              </li>
            ))}
          </ul>

          {/* 8. Coverage & Metrics */}
          <h2 ref={sectionRefs.coverage} className="fade-section">Coverage & Metrics</h2>
          <p className="fade-section">
            <Highlight
              text="Coverage drives closure. Tools include:"
              query={searchTerm}
            />
          </p>
          <ul className="fade-section">
            {[
              "Sim-based coverage: functional, code, assertion, toggle",
              "Formal coverage: proof, property, abstraction",
              "Power-aware coverage: UPF/CPF checks, mode coverage",
            ].map((cov, idx) => (
              <li key={idx}>
                <Highlight text={cov} query={searchTerm} />
              </li>
            ))}
          </ul>

          {/* 9. CI/CD & Pipelines */}
          <h2 ref={sectionRefs.ciCd} className="fade-section">CI/CD & Pipelines</h2>
          <p className="fade-section">
            <Highlight
              text="Integration servers and pipelines often leverage:"
              query={searchTerm}
            />
          </p>
          <ul className="fade-section">
            {[
              "Jenkins, GitLab CI, Azure DevOps for flow orchestration",
              "Docker & Singularity containers for EDA environment reproducibility",
              "Artifact repositories: Nexus, Artifactory for build outputs",
            ].map((ci, idx) => (
              <li key={idx}>
                <Highlight text={ci} query={searchTerm} />
              </li>
            ))}
          </ul>

          {/* 10. Open-Source & Community */}
          <h2 ref={sectionRefs.community} className="fade-section">Open-Source & Community</h2>
          <p className="fade-section">
            <Highlight
              text="Growing ecosystems offer alternative tools:"
              query={searchTerm}
            />
          </p>
          <ul className="fade-section">
            {[
              "Verilator – cycle-accurate open-source simulator",
              "Icarus Verilog – entry-level Verilog simulation",
              "Cocotb – open Python testbench",
              "FOSS flows: OpenROAD, VTR, GHDL",
            ].map((oss, idx) => (
              <li key={idx}>
                <Highlight text={oss} query={searchTerm} />
              </li>
            ))}
          </ul>

          {/* 11. Future Trends */}
          <h2 ref={sectionRefs.trends} className="fade-section">Future Trends</h2>
          <p className="fade-section">
            <Highlight
              text="AI-driven code synthesis, intent-based HDL generation, cloud-native EDA, and standard open APIs promise to reshape the landscape."
              query={searchTerm}
            />
          </p>

          {/* Comment Section */}
          <div className="comment-section" ref={sectionRefs.comments}>
            <h3>3 Comments</h3>

            {/* Comment 1 */}
            <div className="comment-card fade-section">
              <div className="comment-header">
                <img src={ProfileImage} alt="User" className="comment-profile-img" />
                <div>
                  <div className="comment-author-date">
                    <span className="comment-author">HDL Guru</span>
                    <span className="comment-date">May 18, 2025</span>
                  </div>
                </div>
              </div>
              <p className="comment-text">
                Thanks for listing SystemVerilog best practices—very helpful!
              </p>
            </div>

            {/* Comment 2 */}
            <div className="comment-card fade-section">
              <div className="comment-header">
                <img src={ProfileImage} alt="User" className="comment-profile-img" />
                <div>
                  <div className="comment-author-date">
                    <span className="comment-author">OpenEDA Fan</span>
                    <span className="comment-date">May 19, 2025</span>
                  </div>
                </div>
              </div>
              <p className="comment-text">
                Great to see OpenROAD highlighted—excited about the open-source movement!
              </p>
              <button className="reply-button" onClick={() => toggleReply("openeda")}>Reply</button>
              {replyVisible["openeda"] && (
                <div className="leave-comment">
                  <h3>Reply to OpenEDA Fan</h3>
                  <form onSubmit={handleReplySubmit}>
                    <div className="form-group">
                      <label>Name</label>
                      <input type="text" placeholder="Your name..." required />
                    </div>
                    <div className="form-group">
                      <label>Message</label>
                      <textarea placeholder="Your reply..." required />
                    </div>
                    <button type="submit" className="send-message-btn">Reply</button>
                  </form>
                </div>
              )}
            </div>

            {/* Comment 3 */}
            <div className="comment-card fade-section">
              <div className="comment-header">
                <img src={ProfileImage} alt="User" className="comment-profile-img" />
                <div>
                  <div className="comment-author-date">
                    <span className="comment-author">DevOps ASIC</span>
                    <span className="comment-date">May 20, 2025</span>
                  </div>
                </div>
              </div>
              <p className="comment-text">
                CI/CD section nails it—our pipeline runs daily regressions flawlessly now.
              </p>
            </div>

            {/* Leave a comment */}
            <div className="leave-comment fade-section">
              <h3>Leave a comment</h3>
              <p>Have questions or feedback? Fill out the form below!</p>
              <form onSubmit={handleCommentSubmit}>
                <div className="form-group">
                  <label>Name</label>
                  <input type="text" placeholder="Name here..." required />
                </div>
                <div className="form-group">
                  <label>Message</label>
                  <textarea placeholder="Message here…" required />
                </div>
                <button type="submit" className="send-message-btn">Send Message</button>
              </form>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="blog-right-column fade-section">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search keyword..."
              className="search-input"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
            />
            <button onClick={handleSearch} className="search-button">Search</button>
          </div>
          <div className="table-of-contents">
            <h4>In This Article</h4>
            <ul>
              {Object.entries(sectionRefs).map(([key, ref]) => (
                <li key={key}>
                  <button onClick={() => scrollTo(ref)}>
                    {{
                      intro: "Introduction",
                      languages: "Hardware Description Languages",
                      edaTools: "EDA Tools & Suites",
                      scripting: "Scripting & Automation",
                      frameworks: "Verification Frameworks",
                      versionControl: "Version Control",
                      debugTrace: "Debug & Trace Tools",
                      coverage: "Coverage & Metrics",
                      ciCd: "CI/CD & Pipelines",
                      community: "Open-Source & Community",
                      trends: "Future Trends",
                      comments: "Comments",
                    }[key]}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Thank You Popup */}
        <AnimatePresence>
          {showPopup && (
            <motion.div
              className="thank-you-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeOverlay}
            >
              <motion.div
                className="thank-you-content"
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.8 }}
                transition={{ type: "spring", stiffness: 300 }}
                onClick={e => e.stopPropagation()}
              >
                <div className="checkmark-container">
                  <motion.svg
                    className="checkmark"
                    viewBox="0 0 52 52"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 1, ease: "easeInOut" }}
                  >
                    <circle cx="26" cy="26" r="25" stroke="#007bff" strokeWidth="2" />
                    <path
                      d="M16 27.5L23 34.5L36 19.5"
                      stroke="#007bff"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </motion.svg>
                </div>
                <h3>Thank you for the feedback!</h3>
                <p>We appreciate your comment.</p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ToolsAndLanguages;